import React from "react";

import { Card } from "../Card";
import Header from "../Header";
import { UtilityText } from "../UtilityText";

import styles from "./Wizard.scss";

/**
 * Props for a single step in the wizard
 */
export interface StepProps {
  /** Component that represents the current step */
  content: React.ReactNode;
  /** Title for the current step */
  title: string | React.ReactNode;
}

/**
 * Props for the Wizard component
 */
export interface WizardProps {
  /** Array of step objects that represent each step in the wizard */
  steps: StepProps[];
  /** The index of the current step in the steps array. Defaults to 0 */
  currentStepIndex?: number;
  /** True if the wizard is complete; otherwise, we fill in the step progress bar partially depending on the current step */
  complete?: boolean;
  /** Translation for the current step, e.g., "Step 1 of 3" */
  currentStepTranslation?: string;
}

interface MappedStep extends StepProps {
  stepIndex: number;
  stepNumber: number;
  stepWidth: number;
}

/**
 * A custom multi-step process component that displays one step at a time with a progress bar
 */
export function Wizard(props: WizardProps): React.ReactElement | null {
  const {
    steps,
    currentStepIndex = 0,
    complete,
    currentStepTranslation,
  } = props;
  const totalSteps = steps.length;

  const mappedSteps: MappedStep[] = steps.map((s, i) => {
    return {
      ...s,
      stepIndex: i,
      stepNumber: i + 1,
      stepWidth: (100 / totalSteps) * i,
    };
  });
  const currentStep = mappedSteps[currentStepIndex];

  if (!totalSteps) {
    return null;
  }

  const widthPercent = complete === true ? 100 : currentStep.stepWidth;

  return (
    <div data-testid="wizard-container">
      <Card className={styles.container} data-testid="wizard-scroll-content">
        <div className={styles.headerContainer} data-testid="wizard-header">
          <div
            className={styles.headerContainer__title}
            data-testid="wizard-header-title"
          >
            <Header level="2" size="h2">
              {currentStep.title}
            </Header>
          </div>
          {totalSteps > 1 && (
            <div
              className={styles.headerContainer__pages}
              data-testid="wizard-header-pages"
            >
              <UtilityText className={styles.headerCount}>
                {currentStepTranslation
                  ? currentStepTranslation
                  : `Step ${currentStep.stepNumber} of ${totalSteps}`}
              </UtilityText>
            </div>
          )}
        </div>
        <div
          className={styles.progress}
          style={{ width: `${widthPercent}%` }}
        ></div>
        <div className={styles.stepContent} data-testid="wizard-step-content">
          {currentStep.content}
        </div>
      </Card>
    </div>
  );
}
